/**
 * Browser-side layer-service probing, shared by the create flow, the
 * Configuration diagnostics and the inspector's metadata-aware widgets.
 *
 * All requests go through the ArcGIS request stack (never plain fetch) so
 * the IdentityManager tokens and interceptors registered for the preview
 * map authenticate secured services here too. Dynamic imports keep
 * @arcgis/core out of the eager workbench chunk.
 */

export type LayerServiceField = {
  name: string
  alias?: string
  /** ArcGIS field type, e.g. esriFieldTypeString / esriFieldTypeDouble. */
  type?: string
}

export type LayerServiceSublayer = {
  id: number | string
  name: string
  defaultVisibility?: boolean
}

export type LayerServiceMetadata = {
  kind: "arcgis" | "wms"
  /** The layer type a fresh configuration of this source should use. */
  layerTypeCode: "feature" | "map-image" | "group" | "wms" | "scene"
  title: string
  description?: string
  geometryType?: string
  fields: LayerServiceField[]
  sublayers: LayerServiceSublayer[]
  /** Raw ArcGIS service JSON, for consumers needing the full document. */
  raw?: Record<string, unknown>
}

type ArcgisServiceInfo = {
  error?: { message?: string }
  name?: string
  mapName?: string
  documentInfo?: { Title?: string }
  serviceDescription?: string
  description?: string
  type?: string
  geometryType?: string
  fields?: Array<{ name?: string; alias?: string; type?: string }>
  layers?: Array<{
    id?: number
    name?: string
    defaultVisibility?: boolean
    type?: string
  }>
}

/** ArcGIS REST metadata, cached per URL for the lifetime of the page. */
const arcgisServiceInfoCache = new Map<string, Promise<ArcgisServiceInfo>>()

/**
 * Fetches (and caches) the raw `?f=json` description of an ArcGIS REST
 * endpoint — service root or single layer. Failed lookups are evicted so
 * the next call retries instead of pinning a pre-auth "Token Required".
 */
export function fetchArcgisServiceInfo(serviceUrl: string) {
  const cached = arcgisServiceInfoCache.get(serviceUrl)
  if (cached) return cached

  const promise = (async () => {
    const [{ default: esriRequest }, auth] = await Promise.all([
      import("@arcgis/core/request"),
      import("@/v2-backoffice-client/layer/update/preview/layer.preview.auth"),
    ])
    await auth.whenLayerAuthReady()
    const response = await esriRequest(serviceUrl, {
      query: { f: "json" },
      responseType: "json",
    })
    return response.data as ArcgisServiceInfo
  })()

  arcgisServiceInfoCache.set(serviceUrl, promise)
  promise.catch(() => arcgisServiceInfoCache.delete(serviceUrl))
  return promise
}

export function isArcgisRestUrl(url: string) {
  return /\/rest\/services\//i.test(url)
}

function isWmsUrl(url: string) {
  return /(?:[/.]wms|wmsserver|service=wms)/i.test(url)
}

/** Strips a query string and trailing slash for endpoint-shape checks. */
function pathOf(url: string) {
  return url.split(/[?#]/)[0].replace(/\/+$/, "")
}

function isSceneServiceUrl(url: string) {
  return /\/sceneserver(?:\/\d+)?$/i.test(pathOf(url))
}

/** True when the url addresses one layer (…/MapServer/3), not a service. */
function isSingleLayerUrl(url: string) {
  return /\/\d+$/.test(pathOf(url))
}

function toSublayers(
  layers: ArcgisServiceInfo["layers"],
): LayerServiceSublayer[] {
  return (layers ?? [])
    .filter((layer) => layer.id !== undefined && layer.name)
    .map((layer) => ({
      id: layer.id as number,
      name: layer.name as string,
      defaultVisibility: layer.defaultVisibility,
    }))
}

function toFields(fields: ArcgisServiceInfo["fields"]): LayerServiceField[] {
  return (fields ?? [])
    .filter((field) => field.name)
    .map((field) => ({
      name: field.name as string,
      alias: field.alias,
      type: field.type,
    }))
}

async function probeArcgisService(url: string): Promise<LayerServiceMetadata> {
  const info = await fetchArcgisServiceInfo(pathOf(url))
  if (info.error) {
    throw new Error(info.error.message ?? "The service answered an error.")
  }

  const title =
    info.name ?? info.mapName ?? info.documentInfo?.Title ?? "Untitled service"
  const description = info.serviceDescription || info.description || undefined

  const base = {
    kind: "arcgis" as const,
    title,
    description,
    geometryType: info.geometryType,
    fields: toFields(info.fields),
    sublayers: toSublayers(info.layers),
    raw: info as Record<string, unknown>,
  }

  if (isSceneServiceUrl(url)) {
    return { ...base, layerTypeCode: "scene" }
  }
  // A single layer endpoint (…/2) or a service exposing exactly one layer
  // renders as one FeatureLayer; a MapServer root is a MapImageLayer with
  // sublayers; a multi-layer FeatureServer maps to a group of features.
  if (isSingleLayerUrl(url) || info.geometryType) {
    return { ...base, layerTypeCode: "feature" }
  }
  if (/\/mapserver$/i.test(pathOf(url))) {
    return { ...base, layerTypeCode: "map-image" }
  }
  if (base.sublayers.length === 1) {
    return { ...base, layerTypeCode: "feature" }
  }
  return { ...base, layerTypeCode: "group" }
}

async function probeWmsService(url: string): Promise<LayerServiceMetadata> {
  const [{ default: WMSLayer }, auth] = await Promise.all([
    import("@arcgis/core/layers/WMSLayer"),
    import("@/v2-backoffice-client/layer/update/preview/layer.preview.auth"),
  ])
  await auth.whenLayerAuthReady()

  // WMS has no JSON metadata endpoint; loading a throwaway WMSLayer makes
  // the esri capabilities parser do the XML work.
  const wmsLayer = new WMSLayer({ url })
  try {
    await wmsLayer.load()
    return {
      kind: "wms",
      layerTypeCode: "wms",
      title: wmsLayer.title ?? "WMS service",
      description: wmsLayer.description ?? undefined,
      fields: [],
      sublayers: (wmsLayer.sublayers?.toArray() ?? [])
        .filter((sublayer) => sublayer.name)
        .map((sublayer) => ({
          id: sublayer.name as string,
          name: sublayer.title ?? (sublayer.name as string),
          defaultVisibility: sublayer.visible,
        })),
    }
  } finally {
    wmsLayer.destroy()
  }
}

/**
 * The layer type a Configuration (draft text or parsed object) implies,
 * when its shape is unambiguous; null when undecidable. Keeps the stored
 * layer type honest when the configuration changes shape under it — e.g.
 * an AI suggestion turning a group draft (`layers: [...]`) into a flat
 * FeatureLayer config would otherwise render as an EMPTY group: nothing on
 * the map, no error.
 */
export function inferConfigurationLayerType(
  configuration: unknown,
): "feature" | "group" | "map-image" | "wms" | "scene" | null {
  let parsed: unknown = configuration
  if (typeof configuration === "string") {
    try {
      parsed = JSON.parse(configuration)
    } catch {
      return null
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null
  }
  const config = parsed as Record<string, unknown>

  // A layers array is a group composition no matter what the urls are.
  if (Array.isArray(config.layers)) return "group"

  const url = typeof config.url === "string" ? config.url : ""
  if (!url) return null
  const path = pathOf(url)

  if (isWmsUrl(url)) return "wms"
  if (isSceneServiceUrl(url)) return "scene"
  if (!isArcgisRestUrl(url)) return null
  // A single layer endpoint (…/0) or an explicit layerId is one
  // FeatureLayer; so is a FeatureServer root (the factory defaults to
  // layer 0). A MapServer root renders as a MapImageLayer.
  if (/\/\d+$/.test(path) || typeof config.layerId === "number") {
    return "feature"
  }
  if (/\/featureserver$/i.test(path)) return "feature"
  if (/\/mapserver$/i.test(path)) return "map-image"
  return null
}

/**
 * The service url a Configuration draft points at: the `url` key, extended
 * with `/{layerId}` for ArcGIS service roots so probes land on the actual
 * layer document (fields live there, not on the root).
 */
export function getConfigurationServiceUrl(
  configuration: unknown,
): string | null {
  if (!configuration || typeof configuration !== "object") return null
  const config = configuration as Record<string, unknown>
  const url = typeof config.url === "string" ? config.url.trim() : ""
  if (!url) return null
  if (
    isArcgisRestUrl(url) &&
    typeof config.layerId === "number" &&
    !/\/\d+\/?$/.test(pathOf(url))
  ) {
    return `${pathOf(url)}/${config.layerId}`
  }
  return url
}

/**
 * Detects the service behind a layer-server url and returns normalized
 * metadata for prefilling a new layer. Throws with an operator-readable
 * message for unreachable/unrecognized endpoints — the manual path (write
 * the configuration yourself) always remains open.
 */
export async function probeLayerService(
  rawUrl: string,
): Promise<LayerServiceMetadata> {
  const url = rawUrl.trim()
  if (!url) throw new Error("Enter a service URL first.")
  if (!/^https?:\/\//i.test(url)) {
    throw new Error("The URL must start with http(s)://")
  }

  if (isArcgisRestUrl(url)) return probeArcgisService(url)
  if (isWmsUrl(url)) return probeWmsService(url)

  throw new Error(
    "Unrecognized service URL — expected an ArcGIS REST (…/rest/services/…) or WMS endpoint. You can still configure the layer manually.",
  )
}
